import { Link } from "react-router-dom";
import { resolveProductImage } from "@/assets/products";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PRODUCT_THUMB_SIZE = {
  sm: "h-[var(--product-thumb-sm)] w-[var(--product-thumb-sm)]",
  md: "h-[var(--product-thumb-md)] w-[var(--product-thumb-md)]",
  lg: "h-[var(--product-thumb-lg)] w-[var(--product-thumb-lg)]",
} as const;

type ProductThumbSize = keyof typeof PRODUCT_THUMB_SIZE;

interface ProductTableCellProps {
  name: string;
  /** AUT-399: when set, the name becomes a real link so the product opens in a new tab. */
  nameHref?: string;
  asin?: string;
  imageUrl?: string;
  wrapName?: boolean;
  size?: ProductThumbSize;
  asinLinkToAmazon?: boolean;
  className?: string;
}

export function ProductTableCell({
  name,
  nameHref,
  asin,
  imageUrl,
  wrapName = false,
  size = "sm",
  asinLinkToAmazon = false,
  className = "",
}: ProductTableCellProps) {
  const resolvedImage = resolveProductImage(imageUrl || "/placeholder.svg", asin);
  const thumbClasses = PRODUCT_THUMB_SIZE[size];

  const nameClasses = [
    "text-xs font-medium text-foreground",
    wrapName ? "leading-snug" : "truncate",
  ]
    .filter(Boolean)
    .join(" ");

  const thumb = (
    <img
      src={resolvedImage}
      alt={name}
      className={`${thumbClasses} rounded-md border border-border bg-white object-contain`}
    />
  );

  const content = (
    <div className={`flex w-full min-w-0 items-center gap-2.5 pr-4 ${className}`}>
      <div className={`relative ${thumbClasses} shrink-0`}>
        {/*
          AUT-279: the thumb box is square, the Amazon source image is not.
          object-cover centre-cropped it and read as zoomed in / cut off, the
          same defect QA reported on the Products tile. Letterboxing inside the
          box is the accepted trade-off, so keep object-contain here and do not
          reintroduce object-cover — this cell is every list view of a product.
          Reopened: the client rejected the grey bars, so the thumb is bg-white —
          Amazon source images are white-backed and the bars vanish into the photo.
        */}
        {/*
          AUT-399 follow-up: the thumbnail is a second link to the same product, so a row has two
          cmd-clickable targets without the row itself becoming an anchor -- `<tr>` cannot carry a
          stretched overlay reliably, and an `<a>` may not wrap the ASIN link or the row's controls.
          aria-hidden + tabIndex -1 keeps it mouse-only, so a screen reader still hears one link.
        */}
        {nameHref ? (
          <Link
            to={nameHref}
            aria-hidden="true"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            className="block"
          >
            {thumb}
          </Link>
        ) : (
          thumb
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className={nameClasses}>
          {nameHref ? (
            // stopPropagation so the row's own click handler doesn't fire alongside the link.
            <Link to={nameHref} onClick={(e) => e.stopPropagation()} className="hover:underline">
              {name}
            </Link>
          ) : (
            name
          )}
        </p>
        {asin && (
          asinLinkToAmazon ? (
            <a
              href={`https://www.amazon.com/dp/${asin}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-primary hover:underline"
            >
              {asin}
            </a>
          ) : (
            <p className="text-xs text-muted-foreground">{asin}</p>
          )
        )}
      </div>
    </div>
  );

  if (wrapName) return content;

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px]">
          {name}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
